import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Package, DollarSign, TrendingUp, Filter, Eye, ArrowLeft, CreditCard, Wallet } from 'lucide-react';

const ShipmentAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [additionalFilters, setAdditionalFilters] = useState({
    user_id: '',
    status: '',
    vehicle_id: '',
    payment_mode: '',
    location: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        filter,
        ...(filter === 'custom' && customDates),
        ...Object.fromEntries(
          Object.entries(additionalFilters).filter(([_, v]) => v !== '')
        )
      };
      
      const response = await analyticsService.getShipmentAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching shipment analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleApplyFilters = () => {
    fetchData();
    setShowFilters(false);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800">{value}</h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const StatusBreakdown = () => {
    if (!data?.breakdown?.by_status) return null;
    
    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Shipments by Status</h3>
        <div className="space-y-3">
          {Object.entries(data.breakdown.by_status).map(([status, stats]) => (
            <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-card">
              <div className="flex-1">
                <p className="font-medium text-gray-800">{status}</p>
                <p className="text-sm text-gray-600">{stats.count} shipments</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">{formatCurrency(stats.revenue)}</p>
                <p className="text-xs text-green-600">Paid: {formatCurrency(stats.paid)}</p>
                <p className="text-xs text-blue-600">Balance: {formatCurrency(stats.balance)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PaymentModeBreakdown = () => {
    if (!data?.breakdown?.by_payment_mode) return null;
    
    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Shipments by Payment Mode</h3>
        <div className="space-y-3">
          {Object.entries(data.breakdown.by_payment_mode).map(([mode, stats]) => (
            <div key={mode} className="flex items-center justify-between p-3 bg-gray-50 rounded-card">
              <div className="flex-1">
                <p className="font-medium text-gray-800 capitalize">{mode.replace('_', ' ')}</p>
                <p className="text-sm text-gray-600">{stats.count} shipments</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">{formatCurrency(stats.revenue)}</p>
                <p className="text-xs text-green-600">Paid: {formatCurrency(stats.paid)}</p>
                <p className="text-xs text-blue-600">Balance: {formatCurrency(stats.balance)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PaymentHistoryBreakdown = () => {
    if (!data?.breakdown?.payment_history_by_mode) return null;
    
    const paymentModes = [
      { key: 'cash', label: 'Cash', icon: Wallet, color: 'bg-green-500' },
      { key: 'upi', label: 'UPI', icon: CreditCard, color: 'bg-brand' },
      { key: 'cheque', label: 'Cheque', icon: CreditCard, color: 'bg-purple-500' },
      { key: 'bank_transfer', label: 'Bank Transfer', icon: CreditCard, color: 'bg-indigo-500' }
    ];

    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment History by Mode</h3>
        <p className="text-sm text-gray-600 mb-4">Actual payments received through different payment methods</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {paymentModes.map(({ key, label, icon: Icon, color }) => {
            const modeData = data.breakdown.payment_history_by_mode[key] || { payment_count: 0, total_amount: 0 };
            return (
              <div key={key} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-sm p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800">{label}</h4>
                  <div className={`p-2 rounded-full ${color}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-600">Payment Count</p>
                    <p className="text-xl font-bold text-gray-800">{modeData.payment_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Total Amount</p>
                    <p className="text-lg font-semibold text-green-600">{formatCurrency(modeData.total_amount)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Payments Recorded</p>
              <p className="text-xl font-bold text-gray-800">
                {Object.values(data.breakdown.payment_history_by_mode).reduce((sum, mode) => sum + mode.payment_count, 0)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Amount Received</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(
                  Object.values(data.breakdown.payment_history_by_mode).reduce((sum, mode) => sum + mode.total_amount, 0)
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const UserPerformanceTable = () => {
    if (!data?.breakdown?.by_user) return null;

    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6 overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance by User</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Shipments</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collected</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.breakdown.by_user.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.city}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{user.shipment_count}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">{formatCurrency(user.total_revenue)}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-600">{formatCurrency(user.paid)}</td>
                  <td className="px-4 py-3 text-sm text-right text-blue-600">{formatCurrency(user.balance)}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    <button
                      onClick={() => navigate(`/users/view/${user.user_id}`)}
                      className="text-brand hover:text-blue-800"
                    >
                      <Eye className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const VehicleBreakdown = () => {
    if (!data?.breakdown?.by_vehicle) return null;

    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Shipments by Vehicle</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.breakdown.by_vehicle.map((vehicle) => (
            <div key={vehicle.vehicle_id || 'unassigned'} className="p-4 bg-gray-50 rounded-card">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">{vehicle.vehicle_number}</h4>
                <span className="text-sm text-gray-600">{vehicle.shipment_count} shipments</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{vehicle.vehicle_name}</p>
              <p className="text-lg font-bold text-gray-800">{formatCurrency(vehicle.total_revenue)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const LocationBreakdown = () => {
    if (!data?.breakdown?.by_location) return null;

    return (
      <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Locations</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Top Sender Locations</h4>
            <div className="space-y-2">
              {data.breakdown.by_location.top_sender_locations.map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-brand-light rounded">
                  <span className="text-sm text-gray-700">{loc.location}</span>
                  <span className="text-sm font-semibold text-brand">{loc.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Top Receiver Locations</h4>
            <div className="space-y-2">
              {data.breakdown.by_location.top_receiver_locations.map((loc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded">
                  <span className="text-sm text-gray-700">{loc.location}</span>
                  <span className="text-sm font-semibold text-green-600">{loc.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PageLayout title="Shipment Analytics" icon={Package}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Shipment Analytics"
      icon={Package}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Comprehensive shipment performance metrics</p>

                {/* Additional Filters */}
                <div className="bg-white rounded-card shadow-card p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800">Additional Filters</h3>
                    <Button variant="ghost" size="sm" icon={Filter} onClick={() => setShowFilters(!showFilters)}>
                      {showFilters ? 'Hide' : 'Show'} Filters
                    </Button>
                  </div>
                  
                  {showFilters && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                      <input
                        type="text"
                        placeholder="User ID"
                        value={additionalFilters.user_id}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, user_id: e.target.value })}
                        className="input-clean text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Status"
                        value={additionalFilters.status}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, status: e.target.value })}
                        className="input-clean text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Location"
                        value={additionalFilters.location}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, location: e.target.value })}
                        className="input-clean text-sm"
                      />
                      <Button onClick={handleApplyFilters} className="lg:col-span-3 w-full sm:w-auto">Apply Filters</Button>
                    </div>
                  )}
                </div>

                {/* Summary Cards */}
                {data && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Total Shipments"
                        value={data.summary.total_shipments}
                        subtitle="All shipments"
                        icon={Package}
                        color="bg-brand"
                      />
                      <StatCard
                        title="Total Revenue"
                        value={formatCurrency(data.summary.total_revenue)}
                        subtitle="Total amount"
                        icon={DollarSign}
                        color="bg-green-500"
                      />
                      <StatCard
                        title="Paid Amount"
                        value={formatCurrency(data.summary.paid_amount)}
                        subtitle="Collected"
                        icon={TrendingUp}
                        color="bg-emerald-500"
                      />
                      <StatCard
                        title="Balance"
                        value={formatCurrency(data.summary.balance_amount)}
                        subtitle="Pending collection"
                        icon={DollarSign}
                        color="bg-blue-600"
                      />
                    </div>

                    {/* Charges Breakdown */}
                    <div className="bg-white rounded-card shadow-card p-4 lg:p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Charges Breakdown</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Base Charges</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.base_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Labour</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.labour_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Late Charges</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.late_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Delivery</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.delivery_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Discounts</p>
                          <p className="text-lg font-bold text-red-600">-{formatCurrency(data.summary.charges_breakdown.total_discounts)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Breakdowns */}
                    <div className="space-y-6">
                      {/* NEW: Payment History Breakdown */}
                      <PaymentHistoryBreakdown />
                      
                      <StatusBreakdown />
                      <PaymentModeBreakdown />
                      <UserPerformanceTable />
                      <VehicleBreakdown />
                      <LocationBreakdown />
                    </div>
                  </>
                )}
    </PageLayout>
  );
};

export default ShipmentAnalytics;